import React from 'react';
import { css } from 'emotion';

import { getLocale, gettext, ngettext } from '@eventbrite/i18n';

import NotificationBar from '../../components/lean-eds/notification/NotificationBar';
import TicketGeneralAdmissionDialog from '../../components/lean-eds/maps/floating-dialog/general-admission/general-admission-floating-dialog';
import TicketFloatingDialog from '../../components/lean-eds/maps/floating-dialog/tickets/ticket-floating-dialog';
import ManageDashboardHeader from '../../components/lean-eds/maps/manage-dashboard-header';
import ManageDashboardFooter from '../../components/lean-eds/maps/manage-dashboard-footer';
import CheckoutPanel from '../../components/lean-eds/maps/checkout-panel';
import LegendDialog from '../../components/lean-eds/maps/legend-dialog';
import ModalCheckout from '../../components/lean-eds/maps/modal-checkout';
import PrintDialog from '../../components/lean-eds/maps/print-dialog';
import TicketBoughtFloatingDialog from '../../components/lean-eds/maps/floating-dialog/ticket-bought/ticket-bought-floating-dialog';
import { VIEW_MODE_MANAGE_DASHBOARD } from '../../constants/global-constants';
import { MAP_TEMPLATE_OVERLAYS_BACKGROUND_COLOR } from '../../constants/render-constants';
import {
    MANAGE_CHECKOUT_PANEL_WIDTH,
    MANAGE_FOOTER_HEIGHT,
    MANAGE_HEADER_AND_FOOTER_Z_INDEX,
    MANAGE_HEADER_HEIGHT,
} from './constants';
import {
    formatTiersForLegendDialog,
    getFormattedEventDate,
    getPrintOptions,
    getManageOrderUrl,
    parseReleaseHoldInfo,
    parseSeatInfo,
    parseSoldSeatInfo,
    goToPreviousPage,
} from './utils';

const getCheckout = ({
    orderInfo = {},
    removeTicket,
    setAddonQuantity,
    setOrderSummaryTicketPrice,
    syncCart,
    isLoading,
    currencySymbol = '',
}) => {
    const {
        tickets = [],
        addons = [],
        summaryLines,
        totalPriceInfo,
    } = orderInfo;
    const addonsWithQuantity = addons.filter(({ quantity }) => quantity);

    if (tickets.length || addonsWithQuantity.length) {
        return (
            <div
                className={css({
                    backgroundColor: MAP_TEMPLATE_OVERLAYS_BACKGROUND_COLOR,
                    overflow: 'auto',
                    position: 'absolute',
                    top: `${MANAGE_HEADER_HEIGHT}px`,
                    bottom: `${MANAGE_FOOTER_HEIGHT}px`,
                    right: 0,
                    width: `${MANAGE_CHECKOUT_PANEL_WIDTH}px`,
                    display: 'inline',
                    zIndex: 900,
                })}
            >
                <CheckoutPanel
                    tickets={tickets}
                    addons={addons}
                    summaryLines={summaryLines}
                    removeTicketAction={({ id, variantId }) =>
                        removeTicket({
                            seatId: id,
                            variantId,
                        })
                    }
                    setAddonQuantityAction={setAddonQuantity}
                    setOrderSummaryTicketPriceAction={
                        setOrderSummaryTicketPrice
                    }
                    totalPriceInfo={totalPriceInfo}
                    syncCart={syncCart}
                    isLoading={isLoading}
                    currencySymbol={currencySymbol}
                />
            </div>
        );
    }

    return undefined;
};

const getHeader = ({ eventData = {}, totalUnitsInfo = {} }) => {
    const {
        end,
        name: { text: title = '' } = {},
        start,
        locale: eventLocale,
    } = eventData;
    const { total, available } = totalUnitsInfo;
    const locale = getLocale({ eventLocale });

    return (
        <div
            className={css({
                height: `${MANAGE_HEADER_HEIGHT}px`,
                position: 'absolute',
                top: 0,
                width: '100%',
                zIndex: MANAGE_HEADER_AND_FOOTER_Z_INDEX,
            })}
        >
            <ManageDashboardHeader
                eventInfo={{
                    title,
                    datetime: getFormattedEventDate(
                        {
                            start,
                            end,
                        },
                        locale,
                    ),
                    total,
                    available,
                }}
                backAction={() => goToPreviousPage()}
            />
        </div>
    );
};

const getFooter = ({
    isDisabled,
    legendDialogIsVisible,
    tiers,
    toggleLegendDialogVisibility,
    getTicketsAction,
    printDialogIsVisible,
    togglePrintDialogVisibility,
    eventData = {},
    getPDF,
    getPrintOptionValue,
    setPrintOptionValue,
}) => {
    const { name: { text: title = '' } = {} } = eventData;

    return (
        <div
            className={css({
                backgroundColor: MAP_TEMPLATE_OVERLAYS_BACKGROUND_COLOR,
                bottom: 0,
                height: `${MANAGE_FOOTER_HEIGHT}px`,
                position: 'absolute',
                width: '100%',
                zIndex: MANAGE_HEADER_AND_FOOTER_Z_INDEX,
            })}
        >
            <ManageDashboardFooter
                disabled={isDisabled}
                info={{ getTicketsLabel: gettext('Checkout') }}
                legendAction={toggleLegendDialogVisibility}
                getTicketsAction={() => getTicketsAction(true)}
                printAction={togglePrintDialogVisibility}
            />
            {legendDialogIsVisible && (
                <div
                    className={css({
                        position: 'absolute',
                        left: '24px',
                        bottom: '78px',
                    })}
                >
                    <LegendDialog
                        tiers={formatTiersForLegendDialog({ tiers })}
                    />
                </div>
            )}
            {printDialogIsVisible && (
                <div
                    className={css({
                        position: 'absolute',
                        left: '168px',
                        bottom: '78px',
                    })}
                >
                    <PrintDialog
                        printAction={() =>
                            getPDF({
                                fileName: title,
                            })
                        }
                        printOptions={getPrintOptions({
                            getPrintOptionValue,
                        })}
                        onChange={(printOptionId, value) =>
                            setPrintOptionValue({
                                printOptionId,
                                value,
                            })
                        }
                    />
                </div>
            )}
        </div>
    );
};

const getNotifications = ({ notifications }) => [
    <div
        key="info"
        className={css({
            position: 'absolute',
            marginTop: '92px',
            left: '50%',
            transform: 'translateX(-50%)',
        })}
    >
        <div
            className={css({
                minWidth: '736px',
                alignContent: 'center',
                alignItems: 'center',
                justifyContent: 'center',
                margin: 'auto',
                width: 'fit-content',
            })}
        >
            {notifications.map((notification) => {
                const { id, msg, close } = notification;

                return (
                    <NotificationBar
                        key={id}
                        showCloseButton={true}
                        type="neutral"
                        closeHandler={() => close()}
                    >
                        {msg}
                    </NotificationBar>
                );
            })}
        </div>
    </div>,
];

const getTicketSelectionComponent = ({
    ticketSelectionBelongsToGASection,
    chairRadiusInPixels,
    getSectionTotalUnitsInfo,
    getGASectionVariants,
    handleManageHolds,
    handleSelectGATickets,
    handleSelectTicket,
    hasHolds,
    hasSelectedGATickets,
    isSold,
    margins,
    onReleaseAction,
    seatInfo,
    setSelectedGASectionVariantQuantity,
    soldSeatInfo,
    soldSeatInfoFetched,
    ticketPositionType,
    ticketSelectionPosition,
    variants,
    viewMode,
    viewport,
    resetSelectedGATickets,
}) => {
    switch (viewMode) {
        case VIEW_MODE_MANAGE_DASHBOARD: {
            let ticketSelectionComponent = null;

            if (ticketSelectionBelongsToGASection) {
                const { sectionId } = seatInfo;
                const { available, total, availableHolds, totalHolds } =
                    getSectionTotalUnitsInfo(sectionId);
                const quantityHeldLabel = ngettext(
                    // Translators: This is the sentence for when there is only one ticket held.
                    '%(totalHolds)s held',
                    // Translators: This is the sentence for when there are many tickets held.
                    '%(totalHolds)s held',
                    totalHolds,
                    {
                        totalHolds,
                    },
                ).toString();

                ticketSelectionComponent = (
                    <TicketGeneralAdmissionDialog
                        position={ticketSelectionPosition}
                        positionType={ticketPositionType}
                        isDisplay={true}
                        section={{
                            hasHolds: !!totalHolds,
                            unitsAvailable: available,
                            unitsTotal: total,
                            quantityHeldLabel,
                        }}
                        tickets={getGASectionVariants({ sectionId, variants })}
                        viewport={viewport}
                        handleAddSeats={() =>
                            handleSelectGATickets({ sectionId })
                        }
                        handleManageHolds={() =>
                            handleManageHolds({ sectionId })
                        }
                        setTicketQuantityAction={(variantId, quantity) => {
                            setSelectedGASectionVariantQuantity({
                                sectionId,
                                variantId,
                                quantity,
                            });
                        }}
                        verticalOffset={chairRadiusInPixels}
                        disableAddSeatsButton={!hasSelectedGATickets()}
                        margins={margins}
                        cleanup={resetSelectedGATickets}
                    />
                );
            } else if (isSold) {
                if (soldSeatInfoFetched) {
                    ticketSelectionComponent = (
                        <TicketBoughtFloatingDialog
                            position={ticketSelectionPosition}
                            positionType={ticketPositionType}
                            ticket={parseSoldSeatInfo({
                                soldSeatInfo,
                                seatInfo,
                            })}
                            manageOrderUrl={getManageOrderUrl({
                                soldSeatInfo,
                            })}
                            isDisplay={true}
                            viewport={viewport}
                            margins={margins}
                        />
                    );
                }
            } else {
                ticketSelectionComponent = (
                    <TicketFloatingDialog
                        showSeatInfo={true}
                        seatInfo={parseSeatInfo({ seatInfo })}
                        handleSelector={handleSelectTicket}
                        position={ticketSelectionPosition}
                        positionType={ticketPositionType}
                        isDisplay={true}
                        isHold={hasHolds}
                        onReleaseAction={() =>
                            onReleaseAction(
                                parseReleaseHoldInfo({
                                    seatInfo,
                                }),
                            )
                        }
                        tickets={variants}
                        viewport={viewport}
                        verticalOffset={chairRadiusInPixels}
                        margins={margins}
                    />
                );
            }

            return ticketSelectionComponent;
        }
        default:
            return (
                <TicketFloatingDialog
                    handleSelector={handleSelectTicket}
                    position={ticketSelectionPosition}
                    positionType="arrow"
                    seatInfo={seatInfo}
                    isDisplay={true}
                    isHold={false}
                    tickets={variants}
                    viewport={viewport}
                    verticalOffset={chairRadiusInPixels}
                    margins={{}}
                />
            );
    }
};

const getCheckoutModal = ({
    handleClose,
    onPrimaryAction,
    paymentInfo,
    priceInfo,
    disablePrimaryAction,
}) => (
    <ModalCheckout
        handleClose={handleClose}
        onPrimaryAction={onPrimaryAction}
        paymentInfo={paymentInfo}
        priceInfo={priceInfo}
        disablePrimaryAction={disablePrimaryAction}
    />
);

export default {
    getCheckout,
    getCheckoutModal,
    getFooter,
    getHeader,
    getNotifications,
    getTicketSelectionComponent,
};
